import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { errorMessage, unwrap } from 'api/call'
import {
  getFitPlayerProfileRequests,
  putFitPlayerProfileRequestsByIdApprove,
  putFitPlayerProfileRequestsByIdReject,
  type FitPlayerProfileRequest,
  type FitPlayerProfileRequestsIndexResponse,
  type GetFitPlayerProfileRequestsData,
} from 'api/generated'

export type PlayerRequestRow = FitPlayerProfileRequest & {
  loaders: {
    approval: boolean
  }
}

interface PlayersRequestsState {
  loading: boolean
  error: string | null
  playersRequests: PlayerRequestRow[] | null
  page: number
  totalPages: number
}

const initialState: PlayersRequestsState = {
  loading: false,
  error: null,
  playersRequests: null,
  page: 1,
  totalPages: 0,
}

type RequestAction = { requestId: number; notes: string }

export const fetchPlayersRequests = createAsyncThunk<
  FitPlayerProfileRequestsIndexResponse,
  NonNullable<GetFitPlayerProfileRequestsData['query']>,
  { rejectValue: string }
>(
  'playersRequests/fetchPlayersRequests',
  async ({ page, status, notes }, { rejectWithValue }) => {
    try {
      return await unwrap(
        getFitPlayerProfileRequests({
          // empty filters are omitted, as before
          query: {
            page,
            status: status || undefined,
            notes: notes || undefined,
          },
        })
      )
    } catch (error) {
      return rejectWithValue(
        errorMessage(error, 'Failed to fetch players requests.')
      )
    }
  }
)

// Approve can auto-reject on a conflict: the returned request is the truth.
export const approvePlayerRequest = createAsyncThunk<
  FitPlayerProfileRequest,
  RequestAction,
  { rejectValue: string }
>(
  'playersRequests/approve',
  async ({ requestId, notes }, { rejectWithValue }) => {
    try {
      return await unwrap(
        putFitPlayerProfileRequestsByIdApprove({
          path: { id: requestId },
          body: { notes },
        })
      )
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Failed to approve request.'))
    }
  }
)

export const rejectPlayerRequest = createAsyncThunk<
  FitPlayerProfileRequest,
  RequestAction,
  { rejectValue: string }
>(
  'playersRequests/reject',
  async ({ requestId, notes }, { rejectWithValue }) => {
    try {
      return await unwrap(
        putFitPlayerProfileRequestsByIdReject({
          path: { id: requestId },
          body: { notes },
        })
      )
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Failed to reject request.'))
    }
  }
)

const setApprovalLoader = (
  state: PlayersRequestsState,
  requestId: number,
  approval: boolean
) => {
  state.playersRequests?.forEach((req) => {
    if (req.id === requestId) req.loaders.approval = approval
  })
}

const replaceRequest = (
  state: PlayersRequestsState,
  request: FitPlayerProfileRequest
) => {
  if (!state.playersRequests) return
  state.playersRequests = state.playersRequests.map((req) =>
    req.id === request.id ? { ...request, loaders: { approval: false } } : req
  )
}

const playersRequestsSlice = createSlice({
  name: 'playersRequests',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchPlayersRequests.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPlayersRequests.fulfilled, (state, action) => {
        state.loading = false
        state.totalPages = action.payload.total_pages
        state.page = action.payload.page
        state.playersRequests = action.payload.data.map((req) => ({
          ...req,
          loaders: { approval: false },
        }))
      })
      .addCase(fetchPlayersRequests.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Failed to fetch players requests.'
      })
      // Approve
      .addCase(approvePlayerRequest.pending, (state, action) => {
        setApprovalLoader(state, action.meta.arg.requestId, true)
        state.error = null
      })
      .addCase(approvePlayerRequest.fulfilled, (state, action) => {
        replaceRequest(state, action.payload)
      })
      .addCase(approvePlayerRequest.rejected, (state, action) => {
        state.error = action.payload ?? 'Failed to approve request.'
        setApprovalLoader(state, action.meta.arg.requestId, false)
      })
      // Reject
      .addCase(rejectPlayerRequest.pending, (state, action) => {
        setApprovalLoader(state, action.meta.arg.requestId, true)
        state.error = null
      })
      .addCase(rejectPlayerRequest.fulfilled, (state, action) => {
        replaceRequest(state, action.payload)
      })
      .addCase(rejectPlayerRequest.rejected, (state, action) => {
        state.error = action.payload ?? 'Failed to reject request.'
        setApprovalLoader(state, action.meta.arg.requestId, false)
      })
  },
})

export const playersRequestsReducer = playersRequestsSlice.reducer
